import React, {type ReactNode, useContext, useId, useState} from 'react';
import classNames from 'classnames';
import noop from '@tinkoff/utils/function/noop';
import isEmpty from '@tinkoff/utils/is/empty';

import {type Emoji} from '@mattermost/types/emojis';
import {ContextMenu, ContextMenuTrigger, hideMenu} from 'components/widgets/context-menu';
import {addReaction} from 'actions/post_actions';
import EmojiPickerTabs from 'components/emoji_picker/emoji_picker_tabs';
import PostContext from 'components/post_view/post_context';
import {getIsMobileView} from 'selectors/views/browser';
import {useAppDispatch, useAppSelector} from 'stores/redux_store';
import {Locations} from 'utils/constants';
import {isPostEphemeral} from 'utils/utils';

import Menu from 'components/widgets/menu/menu';

import {type Menu as SubMenu} from 'types/store/plugins';

import {usePostMenuItems} from '../use_post_menu_items';
import {usePostMenuShortcuts} from '../use_post_menu_shortcuts';
import {shouldShowPostMenu} from '../utils';

import {type PostMenuItem, type PostMenuItems, type UsePostMenuItemsParams} from '../types';

import styles from './post_context_menu.module.css';

export type PostContextMenuProps =
    Omit<UsePostMenuItemsParams, 'triggerType' | 'onToggleEmojiPicker' | 'location' | 'onLogPostAction'> & {
        open: boolean;
        children: ReactNode;
        disabled: boolean;
        onToggle?: (isOpened: boolean) => void;
    }

type PostContextMenuDropdownProps = Omit<PostContextMenuProps, 'enabled'| 'children'> & {menuId: string};

function renderMenuItem(menuItem: PostMenuItem | null) {
    if (!menuItem) {
        return null;
    }

    if (isEmpty(menuItem.subMenu)) {
        return (
            <Menu.ItemAction
                key={menuItem.attributes.id}
                id={menuItem.attributes.id}
                text={menuItem.text}
                action={menuItem.action}
                icon={menuItem.icon}
                onClick={menuItem.action}
                isDangerous={menuItem.isDangerous}
                rightDecorator={menuItem.shortcut}
                dataQa={menuItem.attributes['data-qa']}
                preventClose={menuItem.preventClose}
            />
        );
    }

    return (
        <Menu.ItemSubMenu
            id={menuItem.attributes.id}
            subMenu={menuItem.subMenu?.map(((subMenuItem) => ({
                action: subMenuItem.action,
                id: subMenuItem.attributes.id,
                parentMenuId: menuItem.attributes.id,
                text: subMenuItem.text,
                itemClassName: subMenuItem.className,
            }) as SubMenu))}
            actionProps={menuItem.attributes.id}
            text={menuItem.text}
            direction={'right'}
            styleSelectableItem={true}
            preventClose={menuItem.preventClose}
        />
    );
}

function renderDivider(key: number | string) {
    return (
        <div
            className='MenuItem__divider'
            key={`menuitemdivider${key}`}
        />
    );
}

function renderPostMenuItems(menuItems: PostMenuItems) {
    return [
        renderMenuItem(menuItems.ADD_EMOJI),
        renderMenuItem(menuItems.REPLY),
        renderDivider(1),
        renderMenuItem(menuItems.SAVE),
        renderMenuItem(menuItems.REMOVE_FROM_SAVED),
        renderMenuItem(menuItems.PLUGINS),
        renderMenuItem(menuItems.DISCUSS_MESSAGE),
        renderMenuItem(menuItems.POST_FOLLOW),
        renderMenuItem(menuItems.MARK_AS_UNREAD),
        renderMenuItem(menuItems.PIN),
        renderMenuItem(menuItems.UNPIN),
        renderMenuItem(menuItems.REACTIONS),
        renderMenuItem(menuItems.COPY_LINK),
        renderDivider(2),
        renderMenuItem(menuItems.COPY_TEXT),
        renderMenuItem(menuItems.EDIT),
        renderMenuItem(menuItems.DELETE),
    ];
}

function renderActivityFeedMenuItems(menuItems: PostMenuItems) {
    return [
        renderMenuItem(menuItems.JUMP),
        renderMenuItem(menuItems.ADD_EMOJI),
        renderMenuItem(menuItems.PLUGINS),
        renderMenuItem(menuItems.DISCUSS_MESSAGE),
        renderMenuItem(menuItems.REACTIONS),
        renderMenuItem(menuItems.SAVE),
        renderMenuItem(menuItems.REMOVE_FROM_SAVED),
        renderMenuItem(menuItems.COPY_LINK),
        renderMenuItem(menuItems.COPY_TEXT),
        renderMenuItem(menuItems.EDIT),
        renderMenuItem(menuItems.DELETE),
    ];
}

function renderSearchMenuItems(menuItems: PostMenuItems) {
    return [
        renderMenuItem(menuItems.JUMP),
        renderMenuItem(menuItems.SAVE),
        renderMenuItem(menuItems.REMOVE_FROM_SAVED),
        renderMenuItem(menuItems.DISCUSS_MESSAGE),
        renderMenuItem(menuItems.PIN),
        renderMenuItem(menuItems.UNPIN),
        renderMenuItem(menuItems.REACTIONS),
        renderMenuItem(menuItems.COPY_LINK),
        renderMenuItem(menuItems.COPY_TEXT),
        renderMenuItem(menuItems.EDIT),
        renderMenuItem(menuItems.DELETE),
    ];
}

function renderMenuItems(menuItems: PostMenuItems, location: UsePostMenuItemsParams['location']) {
    switch (location) {
    case Locations.ACTIVITY_FEED:
        return renderActivityFeedMenuItems(menuItems);
    case Locations.SEARCH:
    case Locations.PINNED:
    case Locations.SAVED:
        return renderSearchMenuItems(menuItems);
    default:
        return renderPostMenuItems(menuItems);
    }
}

const PostContextMenuDropdown = ({
    menuId,
    post,
    onToggle,
    open,
    ...restProps
}: PostContextMenuDropdownProps) => {
    const [openEmojiPicker, setOpenEmojiPicker] = useState(false);
    const dispatch = useAppDispatch();
    const {location, onLogPostAction} = useContext(PostContext);

    const toggleEmojiPicker = () => setOpenEmojiPicker((isOpen) => !isOpen);

    const handleAddEmoji = (emoji: Emoji): void => {
        dispatch(addReaction(post.id, 'short_name' in emoji ? emoji.short_name : emoji.name));
        hideMenu();
    };

    const handleShowMenu = () => {
        onToggle?.(true);
    };

    const handleHideMenu = () => {
        setOpenEmojiPicker(false);
        onToggle?.(false);
    };

    const menuItems = usePostMenuItems({
        ...restProps,
        post,
        location,
        onLogPostAction,
        triggerType: 'rightClick',
        onToggleEmojiPicker: toggleEmojiPicker,
    });

    usePostMenuShortcuts(menuItems, open && !openEmojiPicker, hideMenu);

    return (
        <ContextMenu
            id={menuId}
            className={classNames({[styles.withEmojiPicker]: openEmojiPicker})}
            onHide={handleHideMenu}
            onShow={handleShowMenu}
            placement='forced-right'
            rightOffset={8}
        >
            {openEmojiPicker ? (
                <EmojiPickerTabs
                    className={styles.emojiPicker}
                    onEmojiClose={noop} // mobile only
                    onEmojiClick={handleAddEmoji}
                />
            ) : (
                renderMenuItems(menuItems, location)
            )}
        </ContextMenu>
    );
};

export const PostContextMenu = (props: PostContextMenuProps) => {
    const menuId = useId();
    const isMobile = useAppSelector(getIsMobileView);

    const disabled = props.disabled || isMobile || isPostEphemeral(props.post);

    return (
        <>
            <ContextMenuTrigger id={menuId} holdToDisplay={-1} disable={disabled} shouldOpen={shouldShowPostMenu}>
                {props.children}
            </ContextMenuTrigger>

            {!disabled && <PostContextMenuDropdown {...props} menuId={menuId}/>}
        </>
    );
};
